import { externalUrls } from "@/constants/url";
import { useAnimationFrame } from "@/hooks/use-animation-frame";
import { useHover } from "@/hooks/use-hover";
import { createSoundWave } from "@/lib/audio";
import { cn } from "@/lib/utils";
import { formatMinutes } from "@/utils/time";
import { Icon, Thumbnail, ThumbnailFallback, ThumbnailImage, Typography, useSidebar } from "@backpac/monica";
import { throttle } from "es-toolkit";
import { useEffect, useRef, useState } from "react";
import React from "react";
import type WaveSurfer from "wavesurfer.js";
import { useMusicControls } from "../../context/music-controls-context";
import { useMusic } from "../../hooks/use-music";
import { useMusics } from "../../hooks/use-musics";
import { usePeaks } from "../../hooks/use-peaks";
import { FavoriteMusicButton } from "../favorite-music-button";
import { UseMusicButton } from "../use-music-button";
import { VolumeControl } from "./volume-control";

export function MusicPlayerBar() {
  const { currentMusicAssetId } = useMusicControls();

  if (currentMusicAssetId == null) {
    return null;
  }

  return <MusicPlayer key={currentMusicAssetId} assetId={currentMusicAssetId} />;
}

function MusicPlayer({ assetId }: { assetId: string }) {
  const music = useMusic({ assetId })!;
  const { peaks, duration } = usePeaks(music.isrc);

  const { status, controls } = useMusicControls();
  const [audio, setAudio] = useState<WaveSurfer | null>(null);

  const [ref, isHovered] = useHover<HTMLDivElement>();

  useEffect(() => {
    const audioPlayer = createSoundWave("#waveform", {
      peaks: [peaks],
      duration,
      interactive: true,
      listeners: {
        onPlay: () => controls.play(assetId),
        onPause: () => controls.pause(),
        onFinish: () => controls.finish(),
      },
    });

    audioPlayer.load(`${externalUrls.MUSIC_RESOURCE_BASE_URL}/${music.isrc}.m4a`).catch((error) => {
      if (error.name === "EncodingError") {
        // NOTE: We can ignore it.
      }
    });

    setAudio(audioPlayer);

    return () => {
      audioPlayer.destroy();
    };
  }, [assetId, music.isrc, duration, peaks, controls.play, controls.pause, controls.finish]);

  useEffect(() => {
    if (status === "playing") {
      audio?.play().catch((error) => {
        if (error.name === "AbortError") {
          // NOTE: AbortError is thrown when the audio is already playing. we can ignore it.
          return;
        }

        throw error;
      });
    } else if (status === "paused") {
      audio?.pause();
    }
  }, [status, audio]);

  useEffect(() => {
    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: music.title,
        artwork: [{ src: music.thumbnail }],
      });
    }
  }, [music.title, music.thumbnail]);

  return (
    <MusicPlayer.Container ref={ref}>
      {audio != null ? (
        <MusicPlayer.ProgressBar key={assetId} active={isHovered} audioPlayer={audio} min={0} max={duration} />
      ) : null}
      <MusicPlayer.BackgroundImageContainer>
        <MusicPlayer.Background thumbnail={music.thumbnail} />
        <div className="w-full z-10 flex items-center justify-between gap-1 lg:gap-6 px-3 desktop:px-5 text-white">
          <div className="flex items-center gap-[24px] lg:gap-4 flex-shrink-0">
            <MusicPlayer.Controls />
            <div className="flex items-center gap-3">
              <Thumbnail variant="square" className="size-10 rounded-[6px]">
                <ThumbnailImage src={music.thumbnail} alt={music.title} />
                <ThumbnailFallback>{music.title.slice(0, 2)}</ThumbnailFallback>
              </Thumbnail>
              <Typography size="xs" weight={400} className="flex-shrink-0">
                {music.title}
              </Typography>
            </div>
          </div>

          <div className="flex items-center justify-center gap-[32px] w-full">
            <div className="items-center justify-center gap-2 w-full max-w-[620px] hidden lg:flex">
              <MusicPlayer.CurrentTime audioPlayer={audio} />
              <div key={music.isrc} id="waveform" className="w-full h-[40px]" />
              <Typography size="xs" className="tabular-nums">
                {formatMinutes(duration)}
              </Typography>
            </div>
            <MusicPlayer.VolumeControl audioPlayer={audio} className="hidden lg:flex" />
          </div>

          <div className="flex items-center ml-auto gap-2 lg:gap-3 text-white">
            <FavoriteMusicButton
              assetId={music.assetId}
              isFavorite={music.isFavorite}
              className="text-white hover:bg-[rgba(255,255,255,0.2)]"
            />
            <UseMusicButton
              isrc={music.isrc}
              title={music.title}
              youtubeVideoId={music.ytVideoId}
              thumbnailImageUrl={music.thumbnail}
              className="hover:bg-[rgba(255,255,255,0.2)] text-white"
            />
            <div className="md:hidden">
              <MusicPlayer.PlayButton />
            </div>
          </div>
        </div>
      </MusicPlayer.BackgroundImageContainer>
    </MusicPlayer.Container>
  );
}

MusicPlayer.Container = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(function MusicPlayerContainer(
  { children },
  ref,
) {
  const { open } = useSidebar();

  return (
    <div
      ref={ref}
      className={cn(
        "fixed bottom-0 left-0 right-0 w-full h-[66px] desktop:h-[70px] flex items-center z-[1] shadow-standard",
        {
          "md:left-[240px] md:w-[calc(100%-240px)]": open,
        },
      )}
    >
      {children}
    </div>
  );
});

MusicPlayer.BackgroundImageContainer = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  function MusicPlayerContainer({ children }, ref) {
    return (
      <div ref={ref} className={cn("relative overflow-hidden w-full h-full flex")}>
        {children}
        <div className="absolute inset-0 bg-black opacity-30" />
      </div>
    );
  },
);

MusicPlayer.Background = function MusicPlayerBackground({ thumbnail }: { thumbnail: string }) {
  return (
    <img
      src={thumbnail}
      alt=""
      className="absolute -inset-8 object-cover blur-xl brightness-70 min-w-[calc(100%+4rem)] -translate-y-1/2 pt-1"
    />
  );
};

MusicPlayer.ProgressBar = function MusicPlayerProgressBar({
  active,
  audioPlayer,
  min = 0,
  max = 1,
}: {
  active: boolean;
  audioPlayer: WaveSurfer;
  min?: number;
  max?: number;
}) {
  const [currentTime, setCurrentTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const throttledSetCurrentTime = useRef(
    throttle((time: number) => {
      if (!isDragging) {
        setCurrentTime(time);
      }
    }, 100),
  ).current;

  useEffect(() => {
    const unsubscribe = audioPlayer.on("timeupdate", (time) => {
      if (isDragging) {
        return;
      }

      throttledSetCurrentTime(time);
    });

    return () => {
      unsubscribe();
    };
  }, [audioPlayer, throttledSetCurrentTime, isDragging]);

  const animateTimeChange = useAnimationFrame(setCurrentTime);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = Number(e.target.value);
    animateTimeChange(newTime);
  };

  const progressPercentage = ((currentTime - min) / (max - min)) * 100;

  return (
    <input
      type="range"
      value={currentTime}
      onMouseDown={() => setIsDragging(true)}
      onTouchStart={() => setIsDragging(true)}
      onMouseUp={(e) => {
        const newTime = Number(e.currentTarget.value);
        setIsDragging(false);
        audioPlayer.setTime(newTime);
      }}
      onTouchEnd={(e) => {
        const newTime = Number(e.currentTarget.value);
        setIsDragging(false);
        audioPlayer.setTime(newTime);
      }}
      onChange={handleChange}
      min={min}
      max={max}
      step={0.01}
      className={cn(
        "absolute top-0 left-0 z-20",
        "appearance-none bg-transparent",
        "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:bg-transparent [&::-webkit-slider-thumb]:w-[16px] [&::-webkit-slider-thumb]:h-[16px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:filter [&::-webkit-slider-thumb]:drop-shadow-[0_0_8px_rgba(15,15,15,0.20)]",
        "[&::-webkit-slider-thumb]:transition-all [&::-webkit-slider-thumb]:duration-150",
        "w-full h-[2px] transition-[background] duration-150 ease-in-out",
        { "[&::-webkit-slider-thumb]:bg-white": active },
      )}
      style={{
        background: `linear-gradient(to right, white ${progressPercentage}%, rgba(255, 255, 255, 0.4) ${progressPercentage}%)`,
      }}
    />
  );
};

MusicPlayer.PlayButton = function MusicPlayerPlayButton() {
  const { status, controls } = useMusicControls();

  return (
    <button type="button" className="inline-flex p-2" onClick={() => controls.toggle()}>
      {status === "playing" ? (
        <Icon name="pause-bold" className="size-5" />
      ) : (
        <Icon name="play-fill" className="size-5" />
      )}
    </button>
  );
};

MusicPlayer.Controls = function MusicPlayerControls() {
  const { data: musics } = useMusics();
  const { currentMusicAssetId, controls } = useMusicControls();

  const currentIndex = musics.findIndex((music) => music.assetId === currentMusicAssetId);
  const hasNext = currentIndex < musics.length - 1;
  const hasPrevious = currentIndex > 0;

  return (
    <div className="items-center gap-1 hidden sm:flex">
      <button
        type="button"
        className="inline-flex p-1.5"
        onClick={() => controls.play(musics[currentIndex - 1].assetId)}
        disabled={!hasPrevious}
      >
        <Icon name="arrow-left-wall" className={!hasPrevious ? "opacity-50" : ""} />
      </button>
      <MusicPlayer.PlayButton />
      <button
        type="button"
        className="inline-flex p-1.5"
        onClick={() => controls.play(musics[currentIndex + 1].assetId)}
        disabled={!hasNext}
      >
        <Icon name="arrow-right-wall" className={!hasNext ? "opacity-50" : ""} />
      </button>
    </div>
  );
};

MusicPlayer.CurrentTime = function MusicPlayerCurrentTime({
  audioPlayer,
  className,
}: {
  audioPlayer: WaveSurfer | null;
  className?: string;
}) {
  const [currentTime, setCurrentTime] = useState(0);

  const throttledSetCurrentTime = useRef(
    throttle((time: number) => {
      setCurrentTime(time);
    }, 250),
  ).current;

  useEffect(() => {
    const unsubscribe = audioPlayer?.on("timeupdate", (time) => {
      throttledSetCurrentTime(time);
    });

    return () => {
      unsubscribe?.();
    };
  }, [audioPlayer, throttledSetCurrentTime]);

  return (
    <Typography size="xs" className={cn("tabular-nums", className)}>
      {formatMinutes(currentTime)}
    </Typography>
  );
};

MusicPlayer.VolumeControl = function MusicPlayerVolumeControl({
  audioPlayer,
  className,
}: {
  audioPlayer: WaveSurfer | null;
  className?: string;
}) {
  const [volume, setVolume] = useState(1);

  const throttledSetVolume = useRef(
    throttle((newVolume: number) => {
      audioPlayer?.setVolume(newVolume);
    }, 300),
  ).current;

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    throttledSetVolume(newVolume);
  };

  useEffect(() => {
    audioPlayer?.setVolume(volume);
  }, [audioPlayer, volume]);

  return (
    <VolumeControl
      value={volume}
      onChange={handleVolumeChange}
      onMuteClick={() => setVolume(0)}
      onUnmuteClick={() => setVolume(0.5)}
      className={className}
    />
  );
};
